import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../config/supabase';
import {
  UserSentimentHistoryItem,
  UseSentimentHistoryReturn
} from '../types/sentiment';
import { StockCategoryFilter } from '../types/stockCategories';

interface UseSentimentHistoryOptions {
  limit?: number;
  category?: StockCategoryFilter;
  enabled?: boolean;
}

/**
 * Hook to fetch user's sentiment voting history
 * Only fetches if user is authenticated
 */
export const useSentimentHistory = (
  options: UseSentimentHistoryOptions = {}
): UseSentimentHistoryReturn => {
  const { limit = 50, category, enabled = true } = options;
  const { user } = useAuth();

  const [history, setHistory] = useState<UserSentimentHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    // Don't fetch if disabled or user not authenticated
    if (!enabled || !user?.id) {
      setHistory([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

      // Build query params
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      if (category) {
        params.append('category', category);
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Get Supabase session token
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(
        `${apiBaseUrl}/api/users/${user.id}/sentiment/history?${params.toString()}`,
        { headers }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required');
        }
        if (response.status === 403) {
          throw new Error('Access denied');
        }
        throw new Error(`Failed to fetch sentiment history: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success || !data.data) {
        throw new Error('Invalid response format from API');
      }

      // Transform backend format to frontend format
      const transformedHistory: UserSentimentHistoryItem[] = data.data.history.map((item: any) => ({
        stockSymbol: item.stock_symbol,
        companyName: item.company_name,
        category: item.category,
        userVote: item.user_vote,
        votedAt: item.voted_at,
        datePlayed: item.date_played
      }));

      setHistory(transformedHistory);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch sentiment history';
      console.error('Error fetching sentiment history:', err);
      setError(errorMessage);
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, limit, category, enabled]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    isLoading,
    error,
    refetch: fetchHistory
  };
};
